import { Flex, notify, OrderBy, ProductCard, Typography } from 'front-commons/ds';
import { useCallback, useEffect, useState } from 'react';
import { getPromotionsByProduct } from 'services/products';
import useDrawer from 'stores/drawer';
import { PromotionsDrawerContentProps } from './interfaces';
import { PromotionsDrawerContentContainer } from './styles';
import { useProductSuggestion } from './hooks/useProductSuggestion';
import useOrderImport from 'stores/orderImport';
import useBasket from 'stores/basket';
import usePos from 'stores/pos';
import { getTags } from 'hooks/useCatalogProductRules/helpers';
import { SimplifiedPromotionCardProps } from 'front-commons/ds/components/ProductCard/variants/SimplifiedPromotionCard/interfaces';
import { SimplifiedPromotionCardSkeleton } from 'front-commons/ds/components/ProductCard/variants/SimplifiedPromotionCard/components/Skeleton';
import { PromotionsWithTheProductOrders } from 'services/products/interfaces';

export default function PromotionsDrawerContent({
	data,
	posId = '',
	productId = '',
	orderImportProductId = '',
	handleRefetchItemList,
	useCatalogContext,
	hideSuggestion,
	currentDistributor,
}: PromotionsDrawerContentProps) {
	const [promotions, setPromotions] = useState<SimplifiedPromotionCardProps['data'][]>([]);
	const [order, setOrder] = useState<PromotionsWithTheProductOrders>('DISCOUNT_DESC');
	const [withoutPromotions, setWithoutPromotions] = useState(false);
	const [loading, setLoading] = useState(false);

	const { isDrawerOpen, handleCloseDrawer, handleSetDynamicTitle, handleSetDynamicSubtitle, handleOpenDrawer } =
		useDrawer();
	const {
		basketStore: { basket: basketData },
	} = useBasket();
	const { handleOpenDetailsDrawer } = useOrderImport();
	const {
		posStore: { selectedPos },
	} = usePos();

	const isOpen = isDrawerOpen('promotions-drawer');

	const { handleAddSuggestion, hasPermission, handleLoading, productBasket } = useProductSuggestion({
		data: data?.suggestedProduct ? { ...data.suggestedProduct } : undefined,
		posId,
		onAddSuggestion: data?.suggestedProduct?.onAddSuggestion,
		selectedDistribution: data?.suggestedProduct?.selectedDistribution,
		orderImportProductId: orderImportProductId,
		handleRefetchItemList: data?.suggestedProduct?.handleRefetchItemList,
	});

	const fetchPromotions = useCallback(async () => {
		if (!isOpen) return;

		try {
			setLoading(true);
			setPromotions([]);
			const response = await getPromotionsByProduct({ posId, productId, order });

			const promotionsSubtitle = response.promotions.length > 1 ? 'promoções encontradas' : 'promoção encontrada';

			let dynamicTitle = 'Promoções com o produto';
			let dynamicSubtitle = `${response.promotions.length} ${promotionsSubtitle}`;

			if (!response.promotions.length) {
				setWithoutPromotions(true);
				return;
			}

			if (data?.suggestedProduct?.id && !hideSuggestion) {
				dynamicTitle = 'Sugestões e promoções';
				dynamicSubtitle = '1 sugestão e ' + dynamicSubtitle;
			}

			handleSetDynamicTitle('promotions-drawer', dynamicTitle);
			handleSetDynamicSubtitle('promotions-drawer', dynamicSubtitle);

			setPromotions(response.promotions);
			setWithoutPromotions(false);
		} catch (error) {
			notify.negative({ description: 'Não foi possível buscar as promoções desse produto.' });
			handleCloseDrawer('promotions-drawer');
			setPromotions([]);
		} finally {
			setLoading(false);
		}
	}, [productId, posId, isOpen, data?.suggestedProduct?.id, order]);

	useEffect(() => {
		fetchPromotions();
	}, [fetchPromotions]);

	return (
		<PromotionsDrawerContentContainer withoutPromotions={withoutPromotions}>
			{!loading && data?.suggestedProduct && !hideSuggestion && (
				<Flex direction="column" gap="16px">
					<Typography variant="ParagraphSmall/Semibold">Sugestão de pedido</Typography>

					<ProductCard
						variant="SIMPLIFIED_SUGGESTION"
						data={{ ...data.suggestedProduct }}
						suggestionBackgroundColor="--semantic-success-weak-light"
						suggestionTextColor="--semantic-success-base"
						posId={posId}
						onAddSuggestion={handleAddSuggestion}
						selectedDistribution={data.suggestedProduct.selectedDistribution}
						imageProvider={import.meta.env.VITE_STATIC_STORAGE_BASE}
						orderImportProductId={orderImportProductId}
						handleRefetchItemList={data.suggestedProduct.handleRefetchItemList}
						disabled={!hasPermission || !!productBasket || handleLoading().get(['loading'])}
						waiting={handleLoading().get(['loading'])}
					/>
				</Flex>
			)}

			<Flex display={!loading && !promotions.length ? 'none' : 'flex'} direction="column" gap="16px" padding="0">
				{!loading && data?.suggestedProduct && !hideSuggestion && (
					<Typography variant="ParagraphSmall/Semibold">Promoções</Typography>
				)}

				<OrderBy
					onChange={(e) => setOrder(e.currentTarget?.value as PromotionsWithTheProductOrders)}
					options={[
						{ label: 'A-Z', value: 'DESCRIPTION_ASC' },
						{ label: 'Z-A', value: 'DESCRIPTION_DESC' },
						{ label: 'Vigência', value: 'END_DATE_ASC' },
						{ label: 'Maior Desconto', value: 'DISCOUNT_DESC' },
						{ label: 'Menor Desconto', value: 'DISCOUNT_ASC' },
						{ label: 'Distribuidor', value: 'DISTRIBUTOR' },
					]}
					value={order}
					disabled={loading}
					itemProps={{ width: '500px' }}
				/>

				{loading && Array.generate(5).map((i) => <SimplifiedPromotionCardSkeleton key={i} />)}

				{!loading &&
					promotions.map((promotion, index) => (
						<ProductCard
							key={index}
							variant="SIMPLIFIED_PROMOTION"
							imageProvider={import.meta.env.VITE_STATIC_STORAGE_BASE}
							orderImportProductId={orderImportProductId}
							posId={posId || selectedPos.id}
							productName={data?.description || ''}
							// TODO: adjust typing basketData={basketData as Partial<BasketEntity>}
							basketData={basketData as any}
							tags={getTags(promotion)}
							data={promotion}
							onOpenDetailsDrawer={(props) =>
								handleOpenDetailsDrawer({
									...props,
									initialProductQuantity: { [data!.productId]: data!.initialQuantity },
								})
							}
							onOpenDrawer={handleOpenDrawer}
							currentDistributor={currentDistributor}
							useCatalogContext={useCatalogContext}
							handleRefetchItemList={handleRefetchItemList}
							sourceProductId={productId}
							onOpenChangeDistributorDrawer={() => {
								handleOpenDrawer('catalog-distributor-change', {
									promotionId: promotion.id,
									showReturnButton: true,
								});
							}}
						/>
					))}
			</Flex>

			{!loading && !promotions.length && (
				<Flex padding="16px" justifyContent="center" alignItems="center" gap="8px">
					<Typography align="center" variant="Paragraph/Semibold" color="--text-secondary">
						Não encontramos promoções para esse produto.
					</Typography>
				</Flex>
			)}
		</PromotionsDrawerContentContainer>
	);
}
